// Leafstock v3.0 - Fixed product.list mutation bug
import React, { useEffect, useState, Component, ErrorInfo, ReactNode } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TelegramProvider } from './contexts/TelegramContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { trpc, createTRPCClient } from './lib/trpc';
import { router } from './router';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { WelcomeScreen } from './components/onboarding/WelcomeScreen';
import { StaffSelectionModal } from './components/StaffSelectionModal';
import { Button } from './components/ui/Button';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-dark flex items-center justify-center p-4">
          <div className="bg-dark-card rounded-xl p-6 border border-error/30 max-w-md w-full text-center animate-fadeIn">
            <div className="text-5xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-gray-400 text-sm mb-6">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <Button
              variant="primary"
              onClick={() => window.location.reload()}
              className="w-full"
            >
              🔄 Refresh App
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2, // Retry twice on failure
      retryDelay: 1000, // Wait 1s before retry
      staleTime: 5 * 60 * 1000, // 5 minutes before considering stale
      cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Never refetch if cached data exists
      networkMode: 'always', // Run queries even when offline (WebSocket is disabled)
    },
  },
});

function AppContent() {
  // Show welcome screen for first-time users
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem('leafstock_onboarded');
  });

  const handleOnboardingComplete = () => {
    localStorage.setItem('leafstock_onboarded', 'true');
    setShowWelcome(false);
  };

  const { isLoading, isAuthenticated, staffMember, setStaffMember } = useAuth();

  // Initialize WebSocket connection (only when authenticated)
  // useWebSocket(); // Disabled for better performance - enable when needed

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {/* Welcome Screen - shows for first-time users */}
      {showWelcome && isAuthenticated && (
        <WelcomeScreen onComplete={handleOnboardingComplete} />
      )}

      {/* Staff Selection Modal - shows after auth and onboarding */}
      <StaffSelectionModal
        isOpen={isAuthenticated && !showWelcome && !staffMember}
        onSelect={(name) => setStaffMember(name)}
      />

      <RouterProvider router={router} />
    </>
  );
}

export default function App() {
  const [queryClientInstance] = useState(() => new QueryClient());

  // Initialize immediately to avoid race conditions
  const initData = window.Telegram?.WebApp?.initData || '';
  const [authToken] = useState(initData || 'dev-token');
  const [businessCode] = useState('BRYCE001');

  // Create tRPC client with initial values
  const [trpcClient] = useState(() => createTRPCClient(authToken, businessCode));

  return (
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClientInstance}>
        <QueryClientProvider client={queryClientInstance}>
          <TelegramProvider>
            <AuthProvider>
              <ToastProvider>
                  <AppContent />
                </ToastProvider>
            </AuthProvider>
          </TelegramProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  );
}
